import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError, first } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';

@Injectable()
export class CustomMsalInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private msalService: MsalService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // First check if the request is to a protected resource
    console.debug("CustomMsalInterceptor for request", req.url);
    if (!this.isProtectedResource(req)) {
      return next.handle(req);
    }

    return this.authService.isAuthenticated$.pipe(
      first(), // Take only the current value
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return next.handle(req);
        }

        // User is authenticated, acquire and add token
        return this.addToken(req).pipe(
          switchMap(requestWithToken => next.handle(requestWithToken)),
          catchError(error => {
            if (error.status === 401) {
              console.warn('Token acquisition failed, proceeding without token', error);
              return next.handle(req);
            }
            console.warn('Token acquisition failed, proceeding without token', error);
            return throwError(error);
          })
        );
      })
    );
  }

  private isProtectedResource(req: HttpRequest<any>): boolean {
    const isProtected = req.url.startsWith(environment.backendUrl); // && !req.url.includes('config');
    console.debug(`Checking if ${req.url} requires authorization: ${isProtected}`);

    return isProtected;
  }

  private addToken(req: HttpRequest<any>): Observable<HttpRequest<any>> {
    var currentAuthType = localStorage.getItem('authType') as 'b2c' | 'entraId' | 'guest';  
    if (currentAuthType === 'guest' || !currentAuthType) {
      return from([req]);
    }
    
    const scopes = (window as any)?.sso?.[currentAuthType]?.scopes;
    const account = this.authService.getUser();
    if (!account) {
      return from([req]);
    }
    if (this.authService.isGuestMode()) {
      return from([req]);
    }
    
    return from(
      this.msalService.acquireTokenSilent({
        scopes: [scopes],
        account: account
      })
    ).pipe(
      switchMap(tokenResponse => {
          if (tokenResponse && tokenResponse.idToken) {
            this.authService.setJwtCookie(tokenResponse.idToken);
          }
        const requestWithToken = req.clone({
          setHeaders: {
            Authorization: `Bearer ${tokenResponse.accessToken}`
          }
        });
        return from([requestWithToken]);
      }),
      catchError(error => {
        console.warn('Silent token acquisition failed:', error);
        return from([req]);
      })
    );
  }
}
